import { useEffect, useEffectEvent } from 'react'
import { useQuoteBuilderApi } from '../api/client'
import { readImport } from './api'
import type { DocumentImport, ReviewedRow } from './types'

// The quote AI agent's pass over what the fast match left open (USA-1340). Its proposals land in
// the saved import as it works; the review reads them back until the pass ends.

export type AgentRun = NonNullable<NonNullable<DocumentImport['extraction_result']>['agent']>

const POLL_WORKING_MS = 3000
// A pass paused by a provider outage resumes minutes later.
const POLL_RETRYING_MS = 60_000

export function agentRunning(documentImport: DocumentImport | null) {
  const status = documentImport?.extraction_result?.agent?.status
  return documentImport?.status === 'ready_for_review' && (status === 'working' || status === 'retrying')
}

/** Reads the import again while the agent works, handing each read to `onRead`. */
export function useAgentPass(documentImport: DocumentImport | null, onRead: (item: DocumentImport) => void) {
  const api = useQuoteBuilderApi()
  const read = useEffectEvent(onRead)
  const id = documentImport?.id
  const running = agentRunning(documentImport)
  const status = documentImport?.extraction_result?.agent?.status
  useEffect(() => {
    if (!id || !running) return
    const controller = new AbortController()
    const timer = setInterval(
      () => void readImport(api, id, controller.signal).then(read, () => undefined),
      status === 'retrying' ? POLL_RETRYING_MS : POLL_WORKING_MS,
    )
    return () => {
      clearInterval(timer)
      controller.abort()
    }
  }, [api, id, running, status])
}

/**
 * Each row's match after a read: a row Sales has not chosen for takes the saved choice when there
 * is one (an AQProj row arrives approved) and otherwise the import's starting match, so the
 * agent's proposals replace only what nobody picked.
 */
export function agentMatches(
  rows: ReviewedRow[],
  saved: Record<string, { product_id?: number | null }>,
  current: Record<string, number>,
  touched: Set<string>,
  starting: (row: ReviewedRow) => number | undefined,
) {
  const next = { ...current }
  for (const row of rows) {
    if (touched.has(row.row_id)) continue
    const entry = saved[row.row_id]
    const productId = entry ? (entry.product_id ?? undefined) : starting(row)
    if (productId === undefined) delete next[row.row_id]
    else next[row.row_id] = productId
  }
  return next
}
